import { FC, PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";

import * as amplitude from "@amplitude/analytics-browser";
import { Experiment, ExperimentClient } from "@amplitude/experiment-js-client";
import { autocapturePlugin } from "@amplitude/plugin-autocapture-browser";
import { AMPLITUDE_EXPERIMENT_CLIENT_CONFIG, camelCaseToSnakeCase, AMPLITUDE_GROUP_TYPE } from "@simplyk/common";
import { usePathname } from "next/navigation";

import { LightAmplitudeProvider } from "./LightAmplitudeProvider";

import {
  AMPLITUDE_API_KEY,
  AMPLITUDE_EXPERIMENT_API_KEY,
  AMPLITUDE_SESSION_REPLAY_MASK_SELECTORS,
  AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE,
  AmplitudeEvents,
} from "@/common/constants/amplitude";
import { isProduction, isStaging, isTest } from "@/common/constants/env";
import { AmplitudeContext, GetExperimentValueProps, Primitive } from "@/common/contexts/AmplitudeContext";
import { ConsentModeContext } from "@/common/contexts/ConsentModeContext";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { SessionContext } from "@/common/contexts/SessionContext";
import {
  DASHBOARD_PAGE_URL_PATTERN,
  isDashboardPathname,
  prepareDashboardElementClick,
} from "@/common/helpers/amplitudeAutocapture";
import { getCookieByName } from "@/common/helpers/cookie";
import { parseUserAgent } from "@/common/helpers/parseUserAgent";
import { useDashboardClickAutocaptureFlag } from "@/common/hooks/useDashboardClickAutocaptureFlag";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";

// The autocapture config in init already occupies this package's default plugin name, and add() dedupes by name.
const DASHBOARD_AUTOCAPTURE_PLUGIN_NAME = "zeffy-dashboard-autocapture";
const DASHBOARD_CLICK_ENRICHMENT_PLUGIN_NAME = "zeffy-dashboard-click-enrichment";

const SESSION_REPLAY_PLUGIN_NAME = "@amplitude/plugin-session-replay-browser";

// Default list minus input/select/textarea (no "Element Changed") and contenteditable (cursor clicks carry editor text).
const DASHBOARD_AUTOCAPTURE_SELECTORS = [
  "a",
  "button:not([data-amp-skip-autocapture])",
  "label",
  "video",
  "audio",
  "[data-amp-default-track]",
  ".amp-default-track",
];

const RealAmplitudeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [experimentClient, setExperimentClient] = useState<ExperimentClient | null>(null);
  const [isAmplitudeLoading, setIsAmplitudeLoading] = useState<boolean>(!isTest);
  const { hasFunctionalityConsentMode } = useContext(ConsentModeContext);
  const [initializedConsentMode, setInitializedConsentMode] = useState<boolean | null>(null);
  const isAmplitudeReady = isTest || (!isAmplitudeLoading && initializedConsentMode === hasFunctionalityConsentMode);

  const { sessionId } = useContext(SessionContext);
  const { organization, currentUser } = useCurrentUserContext();
  const { isSmallScreen } = useMediaQuery();
  const pathname = usePathname();
  // This provider also wraps public forms and org pages, so the flag and the plugin stay off outside the dashboard.
  const isDashboardPage = isDashboardPathname(pathname);
  const { isActive: isDashboardAutocaptureActive } = useDashboardClickAutocaptureFlag(isDashboardPage);

  const currentUserOrganizationId = organization?.id;

  useEffect(() => {
    if (isTest) {
      return;
    }
    let isOutdated = false;
    const initAmplitude = async () => {
      try {
        if (hasFunctionalityConsentMode && AMPLITUDE_API_KEY && AMPLITUDE_EXPERIMENT_API_KEY) {
          setIsAmplitudeLoading(true);
          const { plugin: engagementPlugin } = await import("@amplitude/engagement-browser");
          amplitude.add(engagementPlugin());

          const userId = currentUser?.id;

          amplitude.init(AMPLITUDE_API_KEY, {
            autocapture: {
              pageViews: false,
              formInteractions: false,
              fileDownloads: false,
              elementInteractions: {
                pageUrlAllowlist: [/\/grant-finder(\/|\?|#|$)/],
              },
              sessions: true,
            },
            userId,
          });

          const experiment = Experiment.initializeWithAmplitudeAnalytics(AMPLITUDE_EXPERIMENT_API_KEY, {
            fetchOnStart: true,
            automaticFetchOnAmplitudeIdentityChange: true,
            ...AMPLITUDE_EXPERIMENT_CLIENT_CONFIG({ fetchTimeoutMillis: 4000 }),
          });
          await experiment.start();
          setExperimentClient(experiment);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error initializing Amplitude", error);
      } finally {
        if (!isOutdated) {
          setIsAmplitudeLoading(false);
          setInitializedConsentMode(hasFunctionalityConsentMode);
        }
      }
    };
    void initAmplitude();
    return () => {
      isOutdated = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFunctionalityConsentMode]);

  // Added outside init because the allowlist depends on a flag that resolves later and is re-read on an interval.
  useEffect(() => {
    if (isTest || !hasFunctionalityConsentMode || !isDashboardPage || !isDashboardAutocaptureActive) {
      return;
    }
    amplitude.add({
      ...autocapturePlugin({
        pageUrlAllowlist: [DASHBOARD_PAGE_URL_PATTERN],
        cssSelectorAllowlist: DASHBOARD_AUTOCAPTURE_SELECTORS,
        // An empty list drops the div/span click fallback, which would otherwise capture CRM row text.
        actionClickAllowlist: [],
      }),
      name: DASHBOARD_AUTOCAPTURE_PLUGIN_NAME,
    });
    amplitude.add({
      name: DASHBOARD_CLICK_ENRICHMENT_PLUGIN_NAME,
      type: "enrichment",
      execute: async (event) => prepareDashboardElementClick(event, sessionId),
    });

    return () => {
      amplitude.remove(DASHBOARD_AUTOCAPTURE_PLUGIN_NAME);
      amplitude.remove(DASHBOARD_CLICK_ENRICHMENT_PLUGIN_NAME);
    };
  }, [hasFunctionalityConsentMode, isDashboardPage, isDashboardAutocaptureActive, sessionId]);

  useEffect(() => {
    if (isTest || !hasFunctionalityConsentMode || !isDashboardPage) {
      return;
    }
    if (!AMPLITUDE_API_KEY || !AMPLITUDE_EXPERIMENT_API_KEY) {
      return;
    }

    let isCancelled = false;

    const addSessionReplayPlugin = async () => {
      const { sessionReplayPlugin } = await import("@amplitude/plugin-session-replay-browser");
      if (isCancelled) {
        return;
      }
      amplitude.add(
        sessionReplayPlugin({
          sampleRate: AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE,
          privacyConfig: { maskSelector: AMPLITUDE_SESSION_REPLAY_MASK_SELECTORS },
        })
      );
    };
    void addSessionReplayPlugin();

    return () => {
      isCancelled = true;
      amplitude.remove(SESSION_REPLAY_PLUGIN_NAME);
    };
  }, [hasFunctionalityConsentMode, isDashboardPage]);

  const getAmplitudeDeviceId = useCallback(() => {
    return amplitude.getDeviceId();
  }, []);

  const getExperimentValue = useCallback(
    async ({
      flagKey,
      userId,
      organizationId,
      userProperties,
      organizationProperties,
      signal,
    }: GetExperimentValueProps) => {
      // This fetch is necessary to make sure we set the props on the user and get the correct
      // variant value based on the user properties. It seems that identify does not populate
      // the data on the user in time.
      const userProps = userProperties ? formatAmplitudeProperties(userProperties) : undefined;
      const groupProps = organizationProperties ? formatAmplitudeProperties(organizationProperties) : undefined;

      // Check if the operation was cancelled before proceeding
      if (signal?.aborted) {
        return null;
      }

      try {
        await experimentClient?.fetch({
          user_id: userId,
          groups: organizationId ? { [AMPLITUDE_GROUP_TYPE]: [organizationId] } : undefined,
          user_properties: userProps,
          group_properties:
            organizationId && groupProps
              ? {
                  [AMPLITUDE_GROUP_TYPE]: {
                    [organizationId]: groupProps,
                  },
                }
              : undefined,
        });

        return experimentClient?.variant(flagKey);
      } catch (error) {
        if (signal?.aborted) {
          // Check if the operation was cancelled during the fetch. If so,
          return null;
        }
        throw error;
      }
    },
    [experimentClient]
  );

  const logAmplitudeEvent = useCallback(
    (event: AmplitudeEvents | string, payload?: { [key: string]: unknown }) => {
      if (isTest || !hasFunctionalityConsentMode || typeof window === "undefined") {
        return;
      }
      const { browser, os, device } = parseUserAgent();
      const browserVersion = browser.version ?? "";
      const fullPayload = {
        ...payload,
        zeffySessionId: sessionId,
        device: isSmallScreen ? "mobile" : "desktop",
        browser: browser.name ?? "",
        browser_version: browserVersion,
        full_browser_version: browserVersion,
        mobile_vendor: device.vendor ?? "none",
        mobile_model: device.model ?? "none",
        os_name: os.name ?? "",
        os_version: os.version ?? "",
        user_agent: window.navigator?.userAgent,
        fbp: getCookieByName("_fbp"),
        fbc: getCookieByName("_fbc"),
      };
      if (!isProduction) {
        // eslint-disable-next-line no-console
        console.log(event, fullPayload);
      }
      amplitude.track({
        event_type: event,
        event_properties: fullPayload,
        groups: currentUserOrganizationId ? { organization: currentUserOrganizationId } : undefined,
      });
    },
    [hasFunctionalityConsentMode, sessionId, isSmallScreen, currentUserOrganizationId]
  );

  // used to reset the amplitude user on signout
  const resetAmplitude = useCallback(() => {
    if (isTest || !hasFunctionalityConsentMode) {
      return;
    }
    amplitude.reset();
  }, [hasFunctionalityConsentMode]);

  return (
    <AmplitudeContext.Provider
      value={{
        isAmplitudeLoading: !isAmplitudeReady,
        getExperimentValue,
        logAmplitudeEvent,
        resetAmplitude,
        getAmplitudeDeviceId,
      }}
    >
      {children}
    </AmplitudeContext.Provider>
  );
};

const formatAmplitudeProperties = (props: Record<string, Primitive>): { [propertyName: string]: Primitive } => {
  const userProps: { [propertyName: string]: Primitive } = {};
  Object.keys(props).forEach((key) => {
    const v = props[key as keyof typeof props];
    // eslint-disable-next-line eqeqeq
    if (v === null || v === undefined) {
      return;
    }

    const k = camelCaseToSnakeCase(key);
    userProps[k] = v.toString();
  });

  return userProps;
};

export const AmplitudeProvider: FC<PropsWithChildren> = ({ children }) => {
  const useLightMode = !isProduction && !isStaging && process.env.NEXT_PUBLIC_AMPLITUDE_LIGHT_MODE === "true";

  if (useLightMode) {
    return <LightAmplitudeProvider>{children}</LightAmplitudeProvider>;
  }

  return <RealAmplitudeProvider>{children}</RealAmplitudeProvider>;
};
